import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { TabsContent } from '../../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Progress } from '../../components/ui/progress';
import { Plus, Trash2, Eye, Download, AlertCircle, X, Loader2 } from 'lucide-react';
import { getFileTypeInfo, formatFileSize } from '../../utils/fileTypeIcon';
import { getPreviewStatus } from '../../utils/previewPolicy';
import { useDownload, formatBytes } from '../../context/DownloadContext';
import PreviewModal from './PreviewModal';

export const SubmittalsSection = ({
  tabValue,
  submittals,
  selectedSubmittalIds,
  canManageProject,
  canEditCSIItems,
  selectAllItems,
  confirmBulkDelete,
  toggleItemSelection,
  setShowAddSubmittalDialog,
  setSubmittalToDelete,
  setShowDeleteSubmittalModal,
  setTooltipPosition,
  setTooltipContent,
  setTooltipVisible,
  API,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState({ url: '', name: '' });
  const [previewLoading, setPreviewLoading] = useState(null);

  const { startDownload, cancelDownload, getState } = useDownload();

  const openPreview = async (submittal) => {
    setPreviewLoading(submittal.id);
    try {
      const resp = await axios.get(`${API}/submittals/${submittal.id}/preview-url`);
      setPreviewFile({ url: resp.data.url, name: submittal.filename });
      setPreviewOpen(true);
    } catch {
      const fileUrl = submittal.fileUrl?.startsWith('http')
        ? submittal.fileUrl
        : submittal.fileUrl ? `${API}${submittal.fileUrl}` : null;
      if (fileUrl) {
        setPreviewFile({ url: fileUrl, name: submittal.filename });
        setPreviewOpen(true);
      }
    } finally {
      setPreviewLoading(null);
    }
  };

  const handleSecureDownload = (submittal) => {
    const getUrl = async () => {
      const resp = await axios.get(`${API}/submittals/${submittal.id}/download`);
      return resp.data?.url;
    };
    startDownload(
      submittal.id,
      getUrl,
      submittal.filename || 'document',
      submittal.fileSize || 0
    );
  };

  return (
    <TabsContent value={tabValue}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold" data-testid="submittals-title">Submittals</h2>
        <div className="flex gap-2">
          {canManageProject && submittals.length > 0 && (
            <>
              <Button variant="outline" onClick={() => selectAllItems('submittal')} data-testid="submittals-select-all-btn">
                {selectedSubmittalIds.length === submittals.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button
                onClick={() => confirmBulkDelete('submittal')}
                disabled={selectedSubmittalIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
                data-testid="submittals-delete-selected-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedSubmittalIds.length})
              </Button>
            </>
          )}
          {canManageProject && (
            <div className="relative inline-block">
              <Button
                onClick={() => setShowAddSubmittalDialog(true)}
                disabled={submittals.length >= 20}
                data-testid="submittals-add-btn"
                onMouseEnter={(e) => {
                  if (submittals.length >= 20) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltipPosition({ x: rect.left + rect.width / 2, y: rect.top - 8 });
                    setTooltipContent('Maximum 20 submittals allowed');
                    setTooltipVisible(true);
                  }
                }}
                onMouseLeave={() => { if (submittals.length >= 20) setTooltipVisible(false); }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Submittal
              </Button>
            </div>
          )}
        </div>
      </div>

      {submittals.length === 0 ? (
        <Card className="text-center py-12" data-testid="submittals-empty-state">
          <CardContent>
            <AlertCircle className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Submittals Yet</h3>
            <p className="text-slate-600 mb-4">Upload your first submittal document to get started</p>
            {canManageProject && (
              <Button onClick={() => setShowAddSubmittalDialog(true)} data-testid="submittals-empty-add-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Submittal
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="submittals-grid">
          {submittals.map((submittal) => {
            const fileInfo = getFileTypeInfo(submittal.filename);
            const FileIcon = fileInfo.icon;
            const isSelected = selectedSubmittalIds.includes(submittal.id);
            const hasFile = !!(submittal.fileUrl || (submittal.cloudinary && submittal.cloudinary.public_id));
            const preview = getPreviewStatus(submittal);
            const dl = getState(submittal.id);
            const isDownloading = dl.status === 'downloading';
            const isDone = dl.status === 'done';

            return (
              <Card
                key={submittal.id}
                className={`group relative transition-all hover:shadow-md ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50/30' : ''}`}
                data-testid={`submittal-card-${submittal.id}`}
              >
                <CardContent className="p-4">
                  {canEditCSIItems && (
                    <div className="absolute top-3 left-3 z-10">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => toggleItemSelection(submittal.id, 'submittal', { target: { checked } })}
                        data-testid={`submittal-checkbox-${submittal.id}`}
                      />
                    </div>
                  )}

                  <div className={`flex items-center justify-center w-full h-24 rounded-lg mb-3 ${fileInfo.bg}`}>
                    <div className="flex flex-col items-center gap-1">
                      <FileIcon className={`h-10 w-10 ${fileInfo.color}`} />
                      <span className={`text-xs font-bold uppercase ${fileInfo.color}`}>{fileInfo.label}</span>
                    </div>
                  </div>

                  <h3 className="font-semibold text-sm text-slate-900 truncate mb-1" title={submittal.title} data-testid={`submittal-title-${submittal.id}`}>
                    {submittal.title}
                  </h3>

                  {submittal.description && (
                    <p className="text-xs text-slate-500 truncate mb-2" title={submittal.description}>
                      {submittal.description}
                    </p>
                  )}

                  <div className="text-xs text-slate-400 truncate mb-1" title={submittal.filename}>
                    {submittal.filename || 'No file'}
                  </div>
                  {submittal.fileSize && (
                    <div className="text-xs text-slate-400 mb-1">{formatFileSize(submittal.fileSize)}</div>
                  )}
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                    <span className="truncate mr-2" title={`${submittal.uploaderFirstName || ''} ${submittal.uploaderLastName || ''}`.trim()}>
                      {(submittal.uploaderFirstName || submittal.uploaderLastName)
                        ? `Uploaded by ${submittal.uploaderFirstName || ''} ${submittal.uploaderLastName || ''}`.trim()
                        : ''}
                    </span>
                    <span className="shrink-0 text-slate-400">{new Date(submittal.createdAt).toLocaleDateString()}</span>
                  </div>

                  {/* Download progress bar */}
                  {isDownloading && (
                    <div className="mb-2" data-testid={`submittal-progress-${submittal.id}`}>
                      <Progress
                        value={dl.total ? dl.progress : undefined}
                        className="h-1.5 mb-1"
                      />
                      <div className="flex items-center justify-between text-[10px] text-slate-500">
                        <span>
                          {dl.total
                            ? `${dl.progress}% — ${formatBytes(dl.downloaded)} / ${formatBytes(dl.total)}`
                            : `Downloading… ${formatBytes(dl.downloaded)}`}
                        </span>
                        <button
                          onClick={() => cancelDownload(submittal.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          data-testid={`submittal-cancel-download-${submittal.id}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    {hasFile && (
                      <>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full h-8 text-xs"
                                  onClick={() => preview.previewable && openPreview(submittal)}
                                  disabled={!preview.previewable || previewLoading === submittal.id}
                                  data-testid={`submittal-view-${submittal.id}`}
                                >
                                  <Eye className="h-3.5 w-3.5 mr-1" />
                                  {previewLoading === submittal.id ? 'Loading...' : 'Preview'}
                                </Button>
                              </span>
                            </TooltipTrigger>
                            {!preview.previewable && preview.reason && (
                              <TooltipContent side="top" className="max-w-xs text-xs" data-testid={`submittal-preview-tooltip-${submittal.id}`}>
                                {preview.reason}
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                        <Button
                          variant="outline"
                          size="sm"
                          className={`flex-1 h-8 text-xs transition-colors ${isDone ? '!border-green-500 !text-green-600 !bg-green-50' : ''}`}
                          disabled={isDownloading}
                          onClick={() => handleSecureDownload(submittal)}
                          data-testid={`submittal-download-${submittal.id}`}
                        >
                          {isDownloading ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              {dl.total ? `${dl.progress}%` : 'Downloading…'}
                            </>
                          ) : isDone ? (
                            <>
                              <Download className="h-3.5 w-3.5 mr-1" />
                              Downloaded
                            </>
                          ) : (
                            <>
                              <Download className="h-3.5 w-3.5 mr-1" />
                              Download
                            </>
                          )}
                        </Button>
                      </>
                    )}
                    {canEditCSIItems && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="h-8 text-xs px-2"
                        onClick={() => {
                          setSubmittalToDelete(submittal);
                          setShowDeleteSubmittalModal(true);
                        }}
                        data-testid={`submittal-delete-${submittal.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fileUrl={previewFile.url}
        filename={previewFile.name}
      />
    </TabsContent>
  );
};

export default SubmittalsSection;
